import React, { useEffect, useMemo, useState } from 'react';
import { PageSubheader, TicketDetailModal, CreateTicketModal } from '../shared';
import { CustomerSearchBar, CustomerIssuesTable } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { CustomerIssueRow } from './callCenterCustomerIssuesTypes';
import { useTicketsStore } from '../../../store/ticketsStore';
import { uiPriority, uiStatus } from '../shared/ticketAdapter';
import { Ticket } from '../../../services/ticketsApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './CallCenterCustomerIssues.module.css';

function adapt(t: Ticket): CustomerIssueRow {
  return {
    id: String(t.id),
    ticketId: `#${t.ticket_number}`,
    customer: t.customer_name || '—',
    orderId: t.order_id ? formatOrderId(t.order_id) : '———————',
    issueType: t.subject,
    priority: uiPriority(t.priority),
    status: uiStatus(t.status),
    assignedTo: t.assigned_to_name || 'Unassigned',
  };
}

const CallCenterCustomerIssues: React.FC = () => {
  const items = useTicketsStore((s) => s.items);
  const loading = useTicketsStore((s) => s.loading);
  const error = useTicketsStore((s) => s.error);
  const setFilters = useTicketsStore((s) => s.setFilters);
  const fetchList = useTicketsStore((s) => s.fetchList);

  useEffect(() => {
    setFilters({ category: 'customer' });
    fetchList();
  }, [setFilters, fetchList]);

  const allRows = useMemo<CustomerIssueRow[]>(() => items.map(adapt), [items]);
  const [openTicketId, setOpenTicketId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const rows = useMemo<CustomerIssueRow[]>(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((r) => {
      const matchesPriority = priorityFilter === 'All' || r.priority === priorityFilter;
      const matchesStatus = statusFilter === 'All' || r.status === statusFilter;
      const matchesQuery = !q
        || r.customer.toLowerCase().includes(q)
        || r.orderId.toLowerCase().includes(q)
        || r.issueType.toLowerCase().includes(q)
        || r.ticketId.toLowerCase().includes(q);
      return matchesPriority && matchesStatus && matchesQuery;
    });
  }, [allRows, query, priorityFilter, statusFilter]);

  return (
    <div className={styles.container}>
      <PageSubheader
        description="Store complaints — wrong items, missing orders, account holds"
        onAction={() => setShowCreate(true)}
      />
      <CustomerSearchBar
        query={query}
        onQueryChange={setQuery}
        priority={priorityFilter}
        onPriorityChange={setPriorityFilter}
        status={statusFilter}
        onStatusChange={setStatusFilter}
      />
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && rows.length === 0}
        emptyMessage="No customer issues match the current filter"
        onRetry={() => fetchList()}
      >
        <CustomerIssuesTable rows={rows} onView={(id) => setOpenTicketId(parseInt(id, 10))} />
      </AsyncBoundary>
      <TicketDetailModal ticketId={openTicketId} onClose={() => setOpenTicketId(null)} />
      <CreateTicketModal
        open={showCreate}
        defaultCategory="customer"
        lockCategory
        onClose={() => setShowCreate(false)}
        onCreated={(id) => { setOpenTicketId(id); fetchList(); }}
      />
    </div>
  );
};

export const CallCenterCustomerIssuesPage: React.FC = () => <CallCenterCustomerIssues />;

export { CallCenterCustomerIssues };
export default CallCenterCustomerIssues;
